// Locale-aware helpers for the signed-in journey pages (review finding 12). Strings live in
// locales/journey.{en,es,hi,zh}.json; this file only formats dates/numbers/durations with `Intl`
// in the ACTIVE UI language (never the browser default, which can differ from the user's choice)
// and maps enum values (traditions, practice types, Today reasons) onto translation keys.

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PRACTICE_TYPE_LABELS, type PracticeType, type TodayPlan } from '../api/types';
import { TRADITION_LABELS, type Tradition } from '../theme/tokens';

/** `t` plus the active UI language code, for passing to the formatters below. */
export function useJourneyLocale(): { t: TFunction; lang: string } {
  const { t, i18n } = useTranslation();
  return { t, lang: i18n.resolvedLanguage || i18n.language || 'en' };
}

/** Language names in their own script -- used for content/audio language badges, where the
 * reader should recognise the language regardless of the UI language. */
const LANGUAGE_NAMES: Record<string, string> = { en: 'English', es: 'Español', hi: 'हिन्दी', zh: '中文' };

export function languageName(code: string): string {
  return LANGUAGE_NAMES[code] ?? code;
}

/** A language's name in the UI language ("español", "inglés") -- for use inside sentences,
 * where an endonym would read oddly. Falls back to the endonym. */
export function languageNameIn(lang: string, code: string): string {
  try {
    return new Intl.DisplayNames(lang, { type: 'language' }).of(code) ?? languageName(code);
  } catch {
    return languageName(code);
  }
}

/** Premium's list price. Billed in US dollars by Whop, so only the number formatting is
 * localized -- the currency never changes. */
export const PREMIUM_PRICE_USD = 9.99;

export function formatPrice(lang: string, amountUsd: number = PREMIUM_PRICE_USD): string {
  return new Intl.NumberFormat(lang, { style: 'currency', currency: 'USD' }).format(amountUsd);
}

export function formatNumber(lang: string, n: number): string {
  return new Intl.NumberFormat(lang).format(n);
}

/** A plain calendar date ("2026-09-10") as a local date -- never `new Date(iso)`, which reads a
 * date-only string as UTC midnight and can roll to the wrong local day. */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatDate(lang: string, date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(lang, options).format(date);
}

/** Short weekday names starting on Sunday (the calendar grid's first column). */
export function weekdayNamesShort(lang: string): string[] {
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short' });
  // 2023-01-01 was a Sunday.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + i)));
}

/** Stand-alone month name (0-indexed month). */
export function monthName(lang: string, month: number): string {
  return new Intl.DateTimeFormat(lang, { month: 'long' }).format(new Date(2023, month, 1));
}

/** "5 min" / "5 मि॰" / "5分钟" -- the compact duration used in metadata rows. */
export function formatMinutesShort(lang: string, seconds: number, minimumOne = false): string {
  const minutes = Math.round(seconds / 60);
  return new Intl.NumberFormat(lang, { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(
    minimumOne ? Math.max(1, minutes) : minutes,
  );
}

/** "45 seconds" / "3 minutes", in full words, for sentences. */
export function formatDurationLong(lang: string, seconds: number): string {
  if (seconds < 60) {
    return new Intl.NumberFormat(lang, { style: 'unit', unit: 'second', unitDisplay: 'long' }).format(seconds);
  }
  return new Intl.NumberFormat(lang, { style: 'unit', unit: 'minute', unitDisplay: 'long' }).format(
    Math.round(seconds / 60),
  );
}

export function traditionLabel(t: TFunction, tradition: string): string {
  return t(`labels.tradition.${tradition}`, {
    defaultValue: TRADITION_LABELS[tradition as Tradition] ?? tradition,
  });
}

export function practiceTypeLabel(t: TFunction, type: PracticeType): string {
  return t(`labels.practiceType.${type}`, { defaultValue: PRACTICE_TYPE_LABELS[type] ?? type });
}

/** GET /api/today's `explanation`/`nextStep` are server-built English sentences (see
 * docs/LOCALIZATION.md). English keeps the server's text as the source of truth; other languages
 * render the same sentence client-side from the stable `reason` code plus the (already
 * content-localized) practice title. Anything unrecognised falls back to the server text rather
 * than guessing. */
export function localizedPlanText(
  plan: TodayPlan,
  t: TFunction,
  lang: string,
  hasTraditions: boolean,
): { explanation: string; nextStep: string } {
  const server = { explanation: plan.explanation, nextStep: plan.nextStep };
  if (lang === 'en') return server;
  let key: string;
  if (plan.reason === 'none') {
    key = hasTraditions ? 'noPractices' : 'noTraditions';
  } else if (
    plan.reason === 'next_in_path' ||
    plan.reason === 'standalone' ||
    plan.reason === 'premium_preview' ||
    plan.reason === 'review'
  ) {
    if (!plan.practice?.title) return server;
    key = plan.reason;
  } else {
    return server;
  }
  const title = plan.practice?.title ?? '';
  return {
    explanation: t(`plan.${key}.explanation`, { title, price: formatPrice(lang), defaultValue: plan.explanation }),
    nextStep: t(`plan.${key}.nextStep`, { defaultValue: plan.nextStep }),
  };
}
